export type ThemeData = {
  primaryColor: string;
  secondaryColor: string;
  progressBarColor?: string;
  faviconUrl?: string;
  organisationName?: string;
  logoUrl?: string;
  portalsEnabled?: boolean;
};

declare global {
  interface Window {
    __THEME__?: ThemeData;
  }
}

const THEME_STYLE_KEYS = [
  "--primaryBackground",
  "--primaryForeground",
  "--secondaryBackground",
  "--secondaryForeground",
  "--theme-primary-color",
  "--theme-secondary-color",
  "--theme-progress-bar-color",
] as const;

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if ([r, g, b].some((n) => Number.isNaN(n))) {
    return "#ffffff";
  }

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? "#000000" : "#ffffff";
}

export function clearThemeVariablesFromElement(element: HTMLElement): void {
  for (const key of THEME_STYLE_KEYS) {
    element.style.removeProperty(key);
  }
}

export function applyThemeToElement(theme: ThemeData | undefined | null, element: HTMLElement): void {
  if (!theme) {
    element.style.setProperty("--primaryBackground", "#000080");
    element.style.setProperty("--primaryForeground", "#ffffff");
    element.style.setProperty("--secondaryBackground", "#1f2937");
    element.style.setProperty("--secondaryForeground", "#ffffff");
    element.style.setProperty("--theme-primary-color", "#000080");
    element.style.setProperty("--theme-secondary-color", "#1f2937");
    element.style.setProperty("--theme-progress-bar-color", "#000080");
    return;
  }

  if (theme.primaryColor) {
    element.style.setProperty("--primaryBackground", theme.primaryColor);
    element.style.setProperty("--primaryForeground", getContrastColor(theme.primaryColor));
    element.style.setProperty("--theme-primary-color", theme.primaryColor);
    element.style.setProperty("--theme-progress-bar-color", theme.primaryColor);
  }

  const secondaryColor = theme.secondaryColor || "#1f2937";
  element.style.setProperty("--theme-secondary-color", secondaryColor);
  element.style.setProperty("--secondaryBackground", secondaryColor);
  element.style.setProperty("--secondaryForeground", getContrastColor(secondaryColor));

  if (theme.progressBarColor) {
    element.style.setProperty("--theme-progress-bar-color", theme.progressBarColor);
  }
}

export const applyTheme = () => {
  const theme = window.__THEME__;
  applyThemeToElement(theme ?? null, document.documentElement);

  if (theme?.faviconUrl) {
    const faviconLink = document.getElementById("theme-favicon") as HTMLLinkElement;
    if (faviconLink) {
      faviconLink.href = theme.faviconUrl;
    }
  }
};
